import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type { Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { getElementInternals } from "@frontend/bridge/elementInternals";
import { useIslandHost } from "@frontend/contexts";

import { TextEditor } from "./TextEditor";
import {
  type TextEditorDataProps,
  textEditorDataSchema,
} from "./TextEditor.schema";
import { TEXT_EDITOR_TAG_NAME } from "./constants";

export interface TextEditorHandle {
  value: (value: string) => void;
  getValue: () => string;
  disabled: (value?: boolean) => void;
  setError: (message?: string | null) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [TEXT_EDITOR_TAG_NAME]: {
      props: TextEditorDataProps;
      handle: TextEditorHandle;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-text-editor": HTMLElement & TextEditorHandle;
  }
}

export function RWCTextEditor({
  ref,
  value: initialValue,
  disabled: initialDisabled,
  readonly: readOnly,
  inputId,
  tools,
  ...props
}: TextEditorDataProps & { ref?: Ref<TextEditorHandle> }) {
  const host = useIslandHost();

  const [value, setValue] = useState(initialValue ?? "");
  const [disabled, setDisabled] = useState(initialDisabled ?? false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // The handle is copied onto the custom element every time its identity
  // changes, so it reads the document from a ref instead of from the closure.
  const currentValue = useRef(value);

  useEffect(() => {
    currentValue.current = value;
  }, [value]);

  useImperativeHandle(
    ref,
    () => ({
      value: setValue,
      getValue: () => currentValue.current,
      disabled: (nextDisabled = true) => setDisabled(nextDisabled),
      setError: (message = null) => setErrorMessage(message),
    }),
    [],
  );

  const syncFormValue = useCallback(
    (nextValue: string) => {
      getElementInternals(host)?.setFormValue(disabled ? null : nextValue);
    },
    [host, disabled],
  );

  useEffect(() => {
    syncFormValue(value);
  }, [syncFormValue, value]);

  const handleValueChange = useCallback((nextValue: string) => {
    setValue(nextValue);
  }, []);

  return (
    <TextEditor
      {...props}
      id={inputId}
      value={value}
      onValueChange={handleValueChange}
      tools={tools?.length === 0 ? false : tools}
      disabled={disabled}
      readOnly={readOnly}
      invalid={errorMessage !== null}
      title={errorMessage ?? undefined}
    />
  );
}

componentManager.register(
  rwc(TEXT_EDITOR_TAG_NAME, {
    component: RWCTextEditor,
    dataSchema: textEditorDataSchema,
    formAssociated: true,
    usesSlots: true,
    readSlotted: (el) => {
      const attribute = el.getAttribute("value");

      return attribute === null ? { value: el.innerHTML.trim() } : {};
    },
  }),
);
